import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import EmailIcon from '@mui/icons-material/Email';
import ProfileSetup from './ProfileSetup';

function Auth({ onAuthSuccess, userType }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleLoading, setGoogleLoading] = useState(true);
  const [businessDialog, setBusinessDialog] = useState(false);
  const [onboardingMode, setOnboardingMode] = useState(null);
  const [staffJoinData, setStaffJoinData] = useState({
    businessId: '',
    businessName: '',
  });
  const [staffJoinLoading, setStaffJoinLoading] = useState(false);
  const [staffJoinStatusLoading, setStaffJoinStatusLoading] = useState(false);
  const [authTab, setAuthTab] = useState(0); // 0 for Sign In, 1 for Sign Up
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [registrationStep, setRegistrationStep] = useState(0); // 0 for user registration, 1 for business setup
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    businessName: '',
    businessType: 'Retail',
    industry: 'Trading',
    phone: '',
    address: '',
  });
  const [businessData, setBusinessData] = useState({
    name: '',
    type: '',
    address: '',
    phone: '',
    // Profile data
    industry: '',
    description: '',
    employeeCount: '',
    foundedYear: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India',
    website: '',
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const googleSignInInFlightRef = useRef(false);

  const normalizeAccessRole = (role) => {
    const normalizedRole = String(role || '').trim().toLowerCase();
    if (normalizedRole === 'owner' || normalizedRole === 'admin') {
      return 'admin';
    }
    if (normalizedRole === 'staff' || normalizedRole === 'viewer') {
      return 'staff';
    }
    return 'admin';
  };

  const buildBusinessUserData = (userPayload = {}, token = '', overrides = {}) => {
    const userData = {
      id: overrides.id ?? userPayload.id ?? null,
      name: overrides.name ?? userPayload.name ?? '',
      email: overrides.email ?? userPayload.email ?? '',
      userType: 'business_owner',
      accessRole: normalizeAccessRole(userPayload.accessRole ?? overrides.accessRole),
      isStaff: Boolean(userPayload.isStaff ?? overrides.isStaff),
      accessToken: token || overrides.accessToken || '',
      companyId: overrides.companyId ?? null,
    };

    Object.entries(overrides || {}).forEach(([key, value]) => {
      if (value !== undefined) {
        userData[key] = value;
      }
    });

    return userData;
  };

  const parseApiResponse = async (response) => {
    const contentType = response.headers.get('content-type') || '';
    const rawText = await response.text();

    if (!rawText) {
      return { data: null, rawText: '' };
    }

    if (contentType.includes('application/json')) {
      try {
        return { data: JSON.parse(rawText), rawText };
      } catch (jsonError) {
        console.warn('Failed to parse JSON response payload:', jsonError);
      }
    }

    try {
      return { data: JSON.parse(rawText), rawText };
    } catch {
      return { data: null, rawText };
    }
  };

  const shouldResumeOnboarding = (userPayload = {}) => {
    if (!userPayload || typeof userPayload !== 'object') {
      return false;
    }

    if (Boolean(userPayload.accessPending)) {
      return true;
    }

    return Boolean(userPayload.requiresBusinessSetup);
  };

  useEffect(() => {
    // Check if user is already authenticated
    checkAuthStatus();

    // Wait for Google API to load
    const checkGoogleAPI = () => {
      if (window.google || (window.gapi && window.gapi.auth2)) {
        setGoogleLoading(false);
      } else {
        setTimeout(checkGoogleAPI, 100);
      }
    };

    checkGoogleAPI();
  }, []);

  const handleBusinessRegistrationFromProfile = async (businessInfo) => {
    try {
      setError(null);
      setLoading(true);

      console.log('🚀 Starting database-based business registration for:', businessInfo.name);

      // Use the database business registration endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: user.name,
          email: user.email,
          password: 'temp_password_123', // This will be changed later, or we can modify the endpoint
          businessName: businessInfo.name,
          businessType: businessInfo.type || 'Retail',
          industry: businessInfo.industry || 'Trading',
          phone: businessInfo.phone || '',
          address: businessInfo.address || '',
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Business registration failed');
      }

      console.log('✅ Business registration successful:', data);

      // Store authentication data
      const userData = buildBusinessUserData(data.user, data.token, {
        companyId: data.company?.id || null,
      });

      localStorage.setItem('authToken', data.token);
      localStorage.setItem('userData', JSON.stringify(userData));

      // Create business info object
      const completeBusinessInfo = {
        id: data.company.id,
        name: data.company.name,
        type: data.company.businessType,
        industry: data.company.industry,
        address: data.company.address || '',
        phone: data.company.phone || '',
        createdAt: new Date().toISOString(),
        // No sheetId needed for database-only approach
      };

      // Save business data to localStorage
      localStorage.setItem(`business_${userData.id}`, JSON.stringify(completeBusinessInfo));

      setSuccess('Business registration successful!');

      // Complete authentication
      setTimeout(() => {
        onAuthSuccess(userData, completeBusinessInfo);
      }, 2000);

    } catch (error) {
      console.error('Business registration error:', error);
      setError('Business registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const persistAuthUserData = (updatedUserData) => {
    if (!updatedUserData) return;
    localStorage.setItem('userData', JSON.stringify(updatedUserData));
  };

  const handleSubmitStaffJoinRequest = async () => {
    const token = localStorage.getItem('authToken');
    const businessId = parseInt(staffJoinData.businessId, 10);
    const businessName = (staffJoinData.businessName || '').trim();

    if (!token) {
      setError('Session expired. Please log in again.');
      return;
    }

    if (!Number.isInteger(businessId) || businessId <= 0 || !businessName) {
      setError('Enter valid Business ID and Business Name.');
      return;
    }

    try {
      setStaffJoinLoading(true);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/staff/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId,
          businessName,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to submit join request');
      }

      const updatedUser = {
        ...user,
        canRequestBusinessJoin: true,
        accessPending: data.status === 'pending',
        pendingRequest: data.request || null,
      };
      setUser(updatedUser);
      persistAuthUserData(updatedUser);
      setSuccess(data.message || 'Join request submitted successfully.');
      setOnboardingMode(null);
    } catch (joinError) {
      console.error('Staff join request error:', joinError);
      setError(joinError.message || 'Failed to submit join request');
    } finally {
      setStaffJoinLoading(false);
    }
  };

  const handleCheckStaffJoinStatus = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('Session expired. Please log in again.');
      return;
    }

    try {
      setStaffJoinStatusLoading(true);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/staff/request-status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to check request status');
      }

      if (data.status === 'approved' && data.company) {
        const approvedUser = {
          ...user,
          accessRole: normalizeAccessRole(data.accessRole || user?.accessRole),
          isStaff: normalizeAccessRole(data.accessRole || user?.accessRole) !== 'admin',
          accessPending: false,
          pendingRequest: null,
        };
        persistAuthUserData(approvedUser);
        setUser(approvedUser);
        onAuthSuccess(approvedUser, data.company);
        return;
      }

      if (data.status === 'pending') {
        const pendingUser = {
          ...user,
          accessPending: true,
          pendingRequest: data.pendingRequest || user?.pendingRequest || null,
        };
        persistAuthUserData(pendingUser);
        setUser(pendingUser);
        setSuccess('Request is still pending admin approval.');
        return;
      }

      const clearPendingUser = {
        ...user,
        accessPending: false,
        pendingRequest: null,
      };
      persistAuthUserData(clearPendingUser);
      setUser(clearPendingUser);
      setSuccess('No pending request found. You can submit a new request.');
    } catch (statusError) {
      console.error('Staff request status error:', statusError);
      setError(statusError.message || 'Failed to check status');
    } finally {
      setStaffJoinStatusLoading(false);
    }
  };

  const handleSwitchAccount = () => {
    localStorage.clear();
    setUser(null);
    setOnboardingMode(null);
    setBusinessDialog(false);
    setError(null);
    setSuccess(null);
    window.location.assign('/');
  };

  if (user && !user.business) {
    const canRequestBusinessJoin = Boolean(user.canRequestBusinessJoin);

    if (user.accessPending) {
      return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Card sx={{ maxWidth: 560, width: '100%', borderRadius: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Request Pending Approval
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Your request to join a business is pending admin approval.
              </Typography>
              {user?.pendingRequest?.companyName && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Business: {user.pendingRequest.companyName} (ID: {user.pendingRequest.companyId})
                </Alert>
              )}
              <Button
                fullWidth
                variant="contained"
                onClick={handleCheckStaffJoinStatus}
                disabled={staffJoinStatusLoading}
              >
                {staffJoinStatusLoading ? 'Checking...' : 'Check Approval Status'}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={handleSwitchAccount}
                sx={{ mt: 1 }}
              >
                Use Different Account
              </Button>
            </CardContent>
          </Card>
        </Box>
      );
    }

    if (canRequestBusinessJoin && onboardingMode !== 'owner_setup') {
      if (onboardingMode === 'join_business') {
        return (
          <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ maxWidth: 560, width: '100%', borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Join Existing Business
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Enter Business ID and exact Business Name. Admin will approve your request.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Business ID"
                    value={staffJoinData.businessId}
                    onChange={(e) => setStaffJoinData(prev => ({ ...prev, businessId: e.target.value.replace(/[^0-9]/g, '') }))}
                    fullWidth
                  />
                  <TextField
                    label="Business Name"
                    value={staffJoinData.businessName}
                    onChange={(e) => setStaffJoinData(prev => ({ ...prev, businessName: e.target.value }))}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={handleSubmitStaffJoinRequest}
                    disabled={staffJoinLoading}
                  >
                    {staffJoinLoading ? 'Submitting Request...' : 'Submit Join Request'}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setOnboardingMode(null)}
                    disabled={staffJoinLoading}
                  >
                    Back
                  </Button>
                  <Button
                    variant="text"
                    onClick={handleSwitchAccount}
                    disabled={staffJoinLoading}
                  >
                    Use Different Account
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        );
      }

      return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Card sx={{ maxWidth: 620, width: '100%', borderRadius: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Complete Account Setup
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Choose one option to continue.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button variant="contained" onClick={() => setOnboardingMode('owner_setup')}>
                  Create My Business (Admin Access)
                </Button>
                <Button variant="outlined" onClick={() => setOnboardingMode('join_business')}>
                  Join Existing Business (Staff Access)
                </Button>
                <Button variant="text" onClick={handleSwitchAccount}>
                  Back To Home / Use Different Account
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 1200 }}>
          <Button variant="outlined" size="small" onClick={handleSwitchAccount}>
            Use Different Account
          </Button>
        </Box>
        <ProfileSetup
          user={user}
          onComplete={async (profileData) => {

          // Business profile has already been saved by ProfileSetup, so we can proceed directly
          // No need for additional registration call that causes the 409 Conflict

          // Convert profile data to business format for onAuthSuccess
          // Use the company ID from the profileData if available
          const completeBusinessInfo = {
            id: profileData.id || `business_${Date.now()}`,
            name: profileData.name || profileData.businessName,
            type: profileData.type || profileData.businessType,
            industry: profileData.industry,
            address: profileData.address,
            city: profileData.city,
            state: profileData.state,
            zipCode: profileData.zipCode,
            country: profileData.country,
            phone: profileData.phone,
            website: profileData.website,
            description: profileData.description,
            employeeCount: profileData.employeeCount,
            foundedYear: profileData.foundedYear,
            createdAt: new Date().toISOString(),
            sheetId: null, // Will be created during business registration
            masterSheetId: process.env.REACT_APP_GOOGLE_SHEET_ID,
            isNewSheet: true,
          };


          // Complete authentication directly since business is already saved
          onAuthSuccess(user, completeBusinessInfo);
        }}
        onSkip={() => {
          if (canRequestBusinessJoin) {
            setOnboardingMode('join_business');
            return;
          }
          setBusinessDialog(true);
        }}
        />
      </>
    );
  }

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');

      if (token && userData) {
        const parsedUser = JSON.parse(userData);

        // If user is still in onboarding flow, resume onboarding UI directly.
        // Avoid sync/profile calls on refresh when business setup is incomplete.
        if (shouldResumeOnboarding(parsedUser)) {
          const onboardingUser = { ...parsedUser };
          if (onboardingUser.requiresBusinessSetup) {
            delete onboardingUser.business;
            localStorage.setItem('userData', JSON.stringify(onboardingUser));
          }

          setUser(onboardingUser);
          return;
        }

        // Set access token on Google Sheets API if available
        if (parsedUser.accessToken) {
          const { default: googleSheetsAPI } = await import('../shared/api/googleSheets');
          googleSheetsAPI.setAccessToken(parsedUser.accessToken);
        }

        // Check if user already has business data (stored in userData.business)
        let businessData = parsedUser.business;

        if (businessData) {
          

          // Configure Google Sheets API for this business
          const { default: googleSheetsAPI } = await import('../shared/api/googleSheets');
          if (businessData.sheetId) {
            googleSheetsAPI.setBusinessSheet(businessData.sheetId);
            
          }
// Ensure backend knows about this business
try {
  // Try to sync business data from Google Sheets to database
  console.log('🔄 Attempting to sync business data from Google Sheets...');

  const syncResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/sync-sheets`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      sheetId: businessData.sheetId,
      accessToken: parsedUser.accessToken
    }),
  });

  if (syncResponse.ok) {
    const syncResult = await syncResponse.json();
    console.log('✅ Business data sync successful:', syncResult);
  } else {
    console.log('⚠️ Business data sync failed, continuing with local data');
  }

  const businessResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/profile/${parsedUser.id}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (businessResponse.ok) {
    const businessResult = await businessResponse.json();
    if (businessResult.success && businessResult.business) {
      // Backend has the data, sync it
      const updatedUserData = { ...parsedUser, business: businessResult.business };
      localStorage.setItem('userData', JSON.stringify(updatedUserData));
      businessData = businessResult.business;

    } else {

    }
  } else if (businessResponse.status === 404) {
    // Backend doesn't know about this business, try to sync from Google Sheets
    console.log('🔄 Backend missing business data, attempting sync from Google Sheets...');
    const syncResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/sync-sheets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        sheetId: businessData.sheetId,
        accessToken: parsedUser.accessToken
      }),
    });

    if (syncResponse.ok) {
      const syncResult = await syncResponse.json();
      console.log('✅ Business data sync successful:', syncResult);
      // Try to get the data again after sync
      const retryResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/profile/${parsedUser.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (retryResponse.ok) {
        const retryResult = await retryResponse.json();
        if (retryResult.success && retryResult.business) {
          const updatedUserData = { ...parsedUser, business: retryResult.business };
          localStorage.setItem('userData', JSON.stringify(updatedUserData));
          businessData = retryResult.business;
        }
      }
    } else {
      console.log('⚠️ Business data sync failed, continuing with local data');
    }
    // Backend doesn't know about this business, register it



              // Skip business initialization if we already have business data in the database
              // Just proceed with the existing business data
              console.log('✅ Business data already exists, skipping initialization');
              setUser(parsedUser);
              onAuthSuccess(parsedUser, businessData);
              return;

              
              if (!initResponse.ok) {
                const errorText = await initResponse.text();
                
              }

              if (initResponse.ok) {
                
                // Now try to get the data back
                const retryResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/profile/${parsedUser.id}`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                });
                if (retryResponse.ok) {
                  const retryResult = await retryResponse.json();
                  if (retryResult.success && retryResult.business) {
                    const updatedUserData = { ...parsedUser, business: retryResult.business };
                    localStorage.setItem('userData', JSON.stringify(updatedUserData));
                    businessData = retryResult.business;
                    
                  }
                }
              } else {
                
              }
            }
          } catch (backendError) {
            console.log('⚠️ Backend sync failed, continuing with local data:', backendError.message);
          }

          // Proceed with authentication regardless of sync status
          setUser(parsedUser);
          onAuthSuccess(parsedUser, businessData);
        } else {
          // No business in userData, check for existing Google Sheets
          

          try {
            // Check if user has existing business sheets in their Google account
            const { default: googleSheetsAPI } = await import('../shared/api/googleSheets');

            
            
            

            // Search for any sheets containing 'Dhandha Paani Sheet'
            const searchQuery = `name+contains+'Dhandha Paani Sheet'+and+mimeType='application/vnd.google-apps.spreadsheet'`;
            

            const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${searchQuery}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`;
            

            const searchResponse = await fetch(searchUrl, {
              headers: {
                'Authorization': `Bearer ${parsedUser.accessToken}`,
              }
            });

            
            

            if (searchResponse.ok) {
              const searchResult = await searchResponse.json();
              
              

              if (searchResult.files && searchResult.files.length > 0) {
                
                searchResult.files.forEach((file, index) => {
                  
                });

                // Found existing business sheet, use the first one
                const existingSheet = searchResult.files[0];
                console.log(`✅ Using first sheet: ${existingSheet.name} (${existingSheet.id})`);

                // Test if we can access this sheet
                
                try {
                  googleSheetsAPI.setBusinessSheet(existingSheet.id);
                  

                  const dealersData = await googleSheetsAPI.getDealers(); // Quick test to verify access
                  

                  // Extract business name from sheet name (remove ' - Dhandha Paani Sheet')
                  const businessName = existingSheet.name.replace(' - Dhandha Paani Sheet', '');
                  

                  businessData = {
                    id: `business_${Date.now()}`,
                    name: businessName,
                    type: 'Existing Business',
                    address: '',
                    phone: '',
                    createdAt: new Date().toISOString(),
                    sheetId: existingSheet.id,
                    masterSheetId: process.env.REACT_APP_GOOGLE_SHEET_ID,
                    isNewSheet: false,
                  };

                  

                  // Save to userData and localStorage
                  const updatedUserData = { ...parsedUser, business: businessData };
                  localStorage.setItem('userData', JSON.stringify(updatedUserData));
                  localStorage.setItem(`business_${parsedUser.id}`, JSON.stringify(businessData));

                  
                  

                } catch (accessError) {
                  
                  
                  
                  // Continue to backend check
                }
              } else {
                
              }
            } else {
              const errorText = await searchResponse.text();
              
            }

            // If no sheets found or accessible, check backend as fallback
            if (!businessData) {
              

              try {
                const businessResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/profile/${parsedUser.id}`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                });

                if (businessResponse.ok) {
                  const businessResult = await businessResponse.json();
                  if (businessResult.success && businessResult.business) {
                    businessData = businessResult.business;
                    

                    // Save to userData and separate localStorage
                    const updatedUserData = { ...parsedUser, business: businessData };
                    localStorage.setItem('userData', JSON.stringify(updatedUserData));
                    localStorage.setItem(`business_${parsedUser.id}`, JSON.stringify(businessData));

                    // Configure Google Sheets API
                    const { default: googleSheetsAPI } = await import('../shared/api/googleSheets');
                    if (businessData.sheetId) {
                      googleSheetsAPI.setBusinessSheet(businessData.sheetId);
                      
                    }
                  } else {
                    
                  }
                } else {
                  
                }
              } catch (backendError) {
                
                
              }
            }
          } catch (sheetError) {
            
            
          }
        }

        setUser(parsedUser);
        onAuthSuccess(parsedUser, businessData);
      }
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async () => {
    if (!loginData.email || !loginData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Handle successful login
        const userData = buildBusinessUserData(data.user, data.token, {
          companyId: data.company?.id || null,
          canRequestBusinessJoin: Boolean(data.canRequestBusinessJoin),
          accessPending: Boolean(data.accessPending),
          pendingRequest: data.pendingRequest || null,
        });

        // Store authentication data
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userData', JSON.stringify(userData));

        // Check if migration was performed
        if (data.migration && data.migration.success) {
          setSuccess('Login successful! Your Google Sheets data has been migrated to the database.');
        } else {
          setSuccess('Login successful!');
        }

        if (data.accessPending) {
          setOnboardingMode(null);
          setUser(userData);
          setSuccess(data.message || 'Your join request is pending admin approval.');
          return;
        }

        // Check if business setup is required
        if (data.requiresBusinessSetup) {
          // Set user state to trigger ProfileSetup component
          setOnboardingMode(null);
          setUser(userData);
          setSuccess(data.canRequestBusinessJoin
            ? 'Account created! Choose to create a business or request staff access.'
            : 'Account created! Please complete your business setup.');
          return; // Don't complete authentication yet
        }

        // Complete authentication
        setTimeout(() => {
          onAuthSuccess(userData, data.company);
        }, 2000);

      } else {
        setError(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailRegister = async () => {
    if (!registerData.name || !registerData.email || !registerData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (registerData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: registerData.name,
          email: registerData.email,
          password: registerData.password,
          phone: registerData.phone,
          // Business data will be collected separately after user registration
          businessName: '', // Empty for now
          businessType: 'Retail', // Default
          industry: 'Trading', // Default
          address: '', // Empty for now
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store authentication data
        const userData = buildBusinessUserData(data.user, data.token, {
          companyId: data.company?.id || null,
          canRequestBusinessJoin: Boolean(data.canRequestBusinessJoin),
          accessPending: Boolean(data.accessPending),
          pendingRequest: data.pendingRequest || null,
        });

        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userData', JSON.stringify(userData));

        if (data.requiresBusinessSetup) {
          console.log('🏢 Business setup required after registration - showing ProfileSetup component');
          setOnboardingMode(null);
          setUser(userData); // Set user state to show ProfileSetup component
          setSuccess(data.canRequestBusinessJoin
            ? 'Account created! Choose to create a business or request staff access.'
            : 'Account created successfully! Please complete your business setup.');
        } else {
          setSuccess('Account created successfully! Setting up your business...');
        }

        // Close auth dialog - ProfileSetup component will handle business registration
        setShowAuthDialog(false);

        // Reset form
        setRegisterData({
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          phone: '',
          businessName: '',
          businessType: 'Retail',
          industry: 'Trading',
          address: '',
        });
      } else if (response.status === 409) {
        // User already exists and has business - try to log them in
        console.log('User already exists with business, attempting login...');

        // Try to login with the provided credentials
        try {
          const loginResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: registerData.email,
              password: registerData.password,
            }),
          });

          const loginData = await loginResponse.json();

          if (loginResponse.ok && loginData.success) {
            // Handle successful login
            const userData = buildBusinessUserData(loginData.user, loginData.token, {
              companyId: loginData.company?.id || null,
              canRequestBusinessJoin: Boolean(loginData.canRequestBusinessJoin),
              accessPending: Boolean(loginData.accessPending),
              pendingRequest: loginData.pendingRequest || null,
            });

            // Store authentication data
            localStorage.setItem('authToken', loginData.token);
            localStorage.setItem('userData', JSON.stringify(userData));

            // Check if migration was performed
            if (loginData.migration && loginData.migration.success) {
              setSuccess('Login successful! Your Google Sheets data has been migrated to the database.');
            } else {
              setSuccess('Welcome back! Login successful.');
            }

            if (loginData.accessPending) {
              setOnboardingMode(null);
              setUser(userData);
              setSuccess(loginData.message || 'Your join request is pending admin approval.');
              setShowAuthDialog(false);
              return;
            }

            if (loginData.requiresBusinessSetup) {
              setOnboardingMode(null);
              setUser(userData);
              setSuccess(loginData.canRequestBusinessJoin
                ? 'Choose to create a business or request staff access.'
                : 'Please complete your business setup.');
              setShowAuthDialog(false);
              return;
            }

            // Complete authentication
            setTimeout(() => {
              onAuthSuccess(userData, loginData.company);
            }, 2000);

            // Close auth dialog
            setShowAuthDialog(false);
            return;
          } else {
            setError('Account already exists. Please use the Sign In tab to log in.');
          }
        } catch (loginError) {
          console.error('Auto-login failed:', loginError);
          setError('Account already exists. Please use the Sign In tab to log in.');
        }
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const CLIENT_SECRET = process.env.REACT_APP_GOOGLE_CLIENT_SECRET;

    if (!CLIENT_ID) {
      setError('Google Client ID not configured. Please check your .env file.');
      return;
    }

    if (googleLoading) {
      setError('Google Sign-In is still loading. Please wait a moment and try again.');
      return;
    }

    if (googleSignInInFlightRef.current || loading) {
      setError('Google Sign-In is already in progress. Please wait...');
      return;
    }

    console.log('🚀 Starting Google Sign-In process...');
    console.log('🔑 Client ID available:', !!CLIENT_ID);

    // Set loading state for Google sign-in
    googleSignInInFlightRef.current = true;
    setLoading(true);
    setError(null);

    // Production-level OAuth flow with real token exchange
    if (window.google && window.google.accounts) {
      // Use Google Identity Services with proper OAuth flow
      const client = window.google.accounts.oauth2.initCodeClient({
        client_id: CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
        ux_mode: 'popup',
        callback: async (response) => {
          if (response.code) {
            console.log('✅ Google OAuth code received, exchanging for tokens...');
            try {
              // Exchange authorization code for access token
              await exchangeCodeForTokens(response.code);
            } catch (error) {
              console.error('❌ Token exchange failed:', error);
              setError('Failed to complete authentication. Please try again.');
              googleSignInInFlightRef.current = false;
              setLoading(false);
            }
          } else {
            console.log('❌ No authorization code received from Google');
            setError('Google Sign-In failed. Please try again.');
            googleSignInInFlightRef.current = false;
            setLoading(false);
          }
        },
      });

      try {
        client.requestCode();
      } catch (requestError) {
        console.error('âŒ Failed to request Google OAuth code:', requestError);
        setError('Google Sign-In could not start. Please try again.');
        googleSignInInFlightRef.current = false;
        setLoading(false);
      }
    } else if (window.gapi && window.gapi.auth2) {
      // Fallback to older gapi method with proper scopes
      window.gapi.auth2.init({
        client_id: CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile'
      }).then(() => {
        window.gapi.auth2.getAuthInstance().signIn().then(
          (googleUser) => {
            const profile = googleUser.getBasicProfile();
            const authResponse = googleUser.getAuthResponse();

            const userData = {
              ...buildBusinessUserData({
                id: profile.getId(),
                name: profile.getName(),
                email: profile.getEmail(),
                accessRole: 'admin',
                isStaff: false
              }, authResponse.access_token),
              imageUrl: profile.getImageUrl(),
              idToken: authResponse.id_token,
            };

            googleSignInInFlightRef.current = false;
            checkBusinessRegistration(userData);
          },
          (error) => {
            setError('Google Sign-In failed. Please try again.');
            googleSignInInFlightRef.current = false;
            setLoading(false);
          }
        );
      }).catch((error) => {
        setError('Failed to initialize Google Sign-In. Please check your configuration.');
        googleSignInInFlightRef.current = false;
        setLoading(false);
      });
    } else {
      setError('Google Sign-In is not loaded. Please refresh the page.');
      googleSignInInFlightRef.current = false;
      setLoading(false);
    }
  };

  const exchangeCodeForTokens = async (code) => {
    try {
      console.log('🔄 Exchanging authorization code for access token...');

      // This would typically be done on your backend
      // For now, we'll use a simplified approach
      const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET || '', // You might need to add this
          code: code,
          grant_type: 'authorization_code',
          redirect_uri: window.location.origin,
        }),
      });

      const { data: tokenPayload, rawText: tokenRawText } = await parseApiResponse(tokenResponse);
      const tokens = tokenPayload || {};

      if (!tokenResponse.ok) {
        const tokenErrorMessage = tokens?.error_description || tokens?.message || tokenRawText || `Google token exchange failed (${tokenResponse.status})`;
        console.error('Token exchange failed:', tokenErrorMessage);
        setError(tokenErrorMessage);
        setLoading(false);
        return;
      }
      console.log('🔑 Token response received:', {
        hasAccessToken: !!tokens.access_token,
        hasRefreshToken: !!tokens.refresh_token,
        expiresIn: tokens.expires_in,
        tokenType: tokens.token_type
      });

      if (tokens.access_token) {
        console.log('✅ Access token obtained successfully');

        // Get user profile with access token
        const profileResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
          },
        });

        const profile = await profileResponse.json();
        console.log('👤 User profile retrieved:', {
          id: profile.id,
          name: profile.name,
          email: profile.email,
          hasPicture: !!profile.picture
        });

        console.log('🎯 Authenticating with backend using new Google auth endpoint...');

        // Use the new Google authentication endpoint
        try {
          const authResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/google`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              accessToken: tokens.access_token,
              refreshToken: tokens.refresh_token,
              profile: profile
            }),
          });

          const { data: authResult, rawText: authRawText } = await parseApiResponse(authResponse);

          if (!authResponse.ok) {
            if (authResponse.status === 429) {
              const retryAfter = authResponse.headers.get('Retry-After');
              const waitHint = retryAfter
                ? ` Please wait ${retryAfter} seconds and try again.`
                : ' Please wait a minute and try again.';
              setError(`Too many sign-in attempts.${waitHint}`);
            } else {
              setError(authResult?.message || authRawText || `Authentication failed (${authResponse.status})`);
            }
            setLoading(false);
            return;
          }

          if (authResult && authResult.success) {
            console.log('✅ Backend authentication successful:', authResult);

            // Use the database user ID and JWT token from backend
            const userData = {
              ...buildBusinessUserData(authResult.user, authResult.token, {
                companyId: authResult.company?.id || null,
                canRequestBusinessJoin: Boolean(authResult.canRequestBusinessJoin),
                accessPending: Boolean(authResult.accessPending),
                pendingRequest: authResult.pendingRequest || null,
              }),
              imageUrl: profile.picture,
              refreshToken: tokens.refresh_token,
              idToken: tokens.id_token,
              expiresAt: Date.now() + (tokens.expires_in * 1000),
            };

            // Store authentication data
            localStorage.setItem('authToken', authResult.token);
            localStorage.setItem('userData', JSON.stringify(userData));

            if (authResult.accessPending) {
              setOnboardingMode(null);
              setUser(userData);
              setLoading(false);
              setSuccess(authResult.message || 'Your join request is pending admin approval.');
              return;
            }

            // Check if business setup is required
            if (authResult.requiresBusinessSetup) {
              setOnboardingMode(null);
              console.log('🏢 Business setup required - showing ProfileSetup component');
              setUser(userData);
              setLoading(false);
              setSuccess(authResult.canRequestBusinessJoin
                ? 'Account created! Choose to create a business or request staff access.'
                : 'Account created! Please complete your business setup.');
              return;
            }

            // User has business, proceed with authentication
            setUser(userData);
            setLoading(false);
            onAuthSuccess(userData, authResult.company);
            return;
          } else {
            console.error('❌ Backend authentication failed:', authResult);
            setError(authResult?.message || 'Authentication failed');
            setLoading(false);
            return;
          }
        } catch (backendError) {
          console.error('❌ Backend authentication error:', backendError);
          setError('Failed to authenticate with server. Please try again.');
          setLoading(false);
          return;
        }
      } else {
        console.error('❌ No access token in response:', tokens);
        setError('Failed to get access token. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('❌ Token exchange error:', error);
      setError('Failed to complete Google Sign-In. Please try again.');
      setLoading(false);
    } finally {
      googleSignInInFlightRef.current = false;
    }
  };
  const checkForExistingSheets = async (userData, businessName) => {
    try {
      const { default: googleSheetsAPI } = await import('../shared/api/googleSheets');

      

      // Check if there's an existing sheet for this business/user
      const sheetResult = await googleSheetsAPI.checkAndSwitchToBusinessSheet(businessName, userData.email);

      if (sheetResult.success && !sheetResult.isNew) {
        // Found existing sheet, register it with backend first
        

        try {
          // Register the existing sheet with backend

          const initResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/initialize`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${userData.accessToken || localStorage.getItem('authToken')}`,
            },
            body: JSON.stringify({
              sheetId: sheetResult.sheetId,
              businessName: businessName,
              userEmail: userData.email,
            }),
          });

          if (initResponse.ok) {
            
          } else {
            
          }
        } catch (initError) {
          
        }

        // Create business data
        const businessData = {
          id: `business_${Date.now()}`,
          name: businessName,
          type: 'Existing Business',
          address: '',
          phone: '',
          createdAt: new Date().toISOString(),
          sheetId: sheetResult.sheetId,
          masterSheetId: process.env.REACT_APP_GOOGLE_SHEET_ID,
          isNewSheet: false,
        };

        // Save to localStorage
        localStorage.setItem(`business_${userData.id}`, JSON.stringify(businessData));
        completeAuthentication(userData, businessData);
      } else {
        // No existing sheet found or sheet not accessible
        
        
        setUser(userData);
        setBusinessDialog(true);
      }
    } catch (error) {
      
      // If there's an error checking for sheets, show business registration
      setUser(userData);
      setBusinessDialog(true);
    }
  };
  const handleBusinessRegistration = async () => {
    if (!businessData.name || !businessData.type) {
      setError('Please fill in all required fields.');
      return;
    }

    try {
      setError(null);
      setLoading(true);

      console.log('🚀 Starting business setup for:', businessData.name);

      // Since user is already registered, we need to create/update business for existing user
      // We'll use a business initialization endpoint or modify the registration endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/business/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          businessName: businessData.name,
          userEmail: user.email,
          // Add other business data as needed
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Business setup failed');
      }

      console.log('✅ Business setup successful:', data);

      // Create business info object
      const businessInfo = {
        id: data.businessId || `business_${Date.now()}`,
        name: businessData.name,
        type: businessData.type,
        industry: businessData.industry || 'Trading',
        address: businessData.address || '',
        phone: businessData.phone || '',
        city: businessData.city || '',
        state: businessData.state || '',
        zipCode: businessData.zipCode || '',
        country: businessData.country || 'India',
        description: businessData.description || '',
        employeeCount: businessData.employeeCount || '',
        foundedYear: businessData.foundedYear || '',
        website: businessData.website || '',
        createdAt: new Date().toISOString(),
        // Sheet info will be populated by the backend
        sheetId: data.sheetId || '',
        masterSheetId: data.masterSheetId || '',
        isNewSheet: data.isNewSheet || true,
      };

      // Update user data with business info
      const updatedUserData = { ...user, business: businessInfo };
      localStorage.setItem('userData', JSON.stringify(updatedUserData));
      localStorage.setItem(`business_${user.id}`, JSON.stringify(businessInfo));

      setSuccess('Registration completed successfully!');

      // Complete authentication
      setTimeout(() => {
        setBusinessDialog(false);
        onAuthSuccess(updatedUserData, businessInfo);
      }, 2000);

    } catch (error) {
      console.error('Business setup error:', error);
      setError('Business setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const completeAuthentication = async (userData, businessData) => {
    const authData = {
      ...userData,
      business: businessData,
    };

    // Store authentication data
    localStorage.setItem('authToken', userData.accessToken || 'authenticated');
    localStorage.setItem('userData', JSON.stringify(authData));

    setUser(authData);
    onAuthSuccess(authData);
  };

  const handleSignOut = async () => {
    try {
      

      // Sign out from Google OAuth completely
      if (window.google && window.google.accounts && window.google.accounts.oauth2) {
        // For Google Identity Services OAuth
        
        // Note: GIS doesn't have a direct sign-out method, but we can revoke the token
        const token = localStorage.getItem('authToken');
        if (token) {
          // Revoke the access token
          fetch(`https://oauth2.googleapis.com/revoke?token=${token}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }).catch(err => console.log('Token revocation failed:', err));
        }
      }

      if (window.gapi && window.gapi.auth2) {
        // For older gapi method
        
        const authInstance = window.gapi.auth2.getAuthInstance();
        if (authInstance) {
          authInstance.signOut().then(() => {
            
          }).catch(err => console.log('gapi sign-out error:', err));
        }
      }

      // Clear ALL local storage data completely
      
      localStorage.clear();

      // Reset all state
      setUser(null);
      setBusinessDialog(false);
      setBusinessData({
        name: '',
        type: '',
        address: '',
        phone: '',
        // Profile data
        industry: '',
        description: '',
        employeeCount: '',
        foundedYear: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'India',
        website: '',
      });

      // Clear any cached data in the API instances
      try {
        const { default: googleSheetsAPI } = await import('../shared/api/googleSheets');
        const { default: appsScriptAPI } = await import('../shared/api/appsScript');

        // Reset Google Sheets API to master sheet
        if (googleSheetsAPI && googleSheetsAPI.resetToMasterSheet) {
          googleSheetsAPI.resetToMasterSheet();
        }

        // Reset Apps Script API if available
        if (appsScriptAPI && appsScriptAPI.reset) {
          appsScriptAPI.reset();
        }
      } catch (error) {
        
      }

      

      // Only reload if user is not actively working to prevent data loss
      // Check if user has been active in the last 30 seconds
      const lastActivity = localStorage.getItem('lastUserActivity');
      const isRecentlyActive = lastActivity && (Date.now() - parseInt(lastActivity)) < 30000; // 30 seconds

      if (!isRecentlyActive) {
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        console.log('User was recently active, skipping page reload during sign-out');
      }

    } catch (error) {
      
      // Force clear everything even if there's an error
      localStorage.clear();
      setUser(null);

      // Only reload if not recently active
      const lastActivity = localStorage.getItem('lastUserActivity');
      const isRecentlyActive = lastActivity && (Date.now() - parseInt(lastActivity)) < 30000;

      if (!isRecentlyActive) {
        window.location.reload();
      }
    }
  };

  const handleBusinessInputChange = (field, value) => {
    setBusinessData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }


  // If customer authentication, show different UI
  if (userType === 'customer') {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
        {/* Customer Auth Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            py: { xs: 8, md: 12 },
            px: 4,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Ccircle cx="7" cy="7" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}
          />

          <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1200, mx: 'auto' }}>
            {/* Logo/Icon and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '20px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 3,
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="Dhandhapaani"
                  sx={{ width: 52, height: 52, borderRadius: '14px', objectFit: 'cover' }}
                />
              </Box>
              <Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    mb: 1,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                  }}
                >
                  Customer Portal
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    opacity: 0.9,
                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                  }}
                >
                  Shop Online & Manage Orders
                </Typography>
              </Box>
            </Box>

            {/* Customer Auth Form */}
            <Box sx={{ maxWidth: 400, mx: 'auto', mb: 6 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  backdropFilter: 'blur(10px)',
                  background: 'rgba(255, 255, 255, 0.95)',
                  color: 'black',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, textAlign: 'center' }}>
                    Sign In to Shop
                  </Typography>

                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleGoogleSignIn}
                      disabled={googleLoading || loading}
                      startIcon={<GoogleIcon />}
                      sx={{
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        background: '#4285f4',
                        '&:hover': {
                          background: '#3367d6',
                        },
                        textTransform: 'none',
                      }}
                    >
                      {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Continue with Google'}
                    </Button>

                    <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                      <Box sx={{ flex: 1, height: 1, background: '#e0e0e0' }} />
                      <Typography sx={{ px: 2, color: '#666', fontSize: '0.9rem' }}>or</Typography>
                      <Box sx={{ flex: 1, height: 1, background: '#e0e0e0' }} />
                    </Box>

                    <Typography variant="body2" sx={{ textAlign: 'center', color: '#666', mb: 2 }}>
                      New customer? Your account will be created automatically.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Features */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 4, maxWidth: 1000, mx: 'auto' }}>
              {[
                {
                  icon: '🛒',
                  title: 'Easy Shopping',
                  description: 'Browse products, add to cart, and checkout in minutes'
                },
                {
                  icon: '📦',
                  title: 'Order Tracking',
                  description: 'Track your orders in real-time from placement to delivery'
                },
                {
                  icon: '💳',
                  title: 'Secure Payments',
                  description: 'Safe and secure payment processing for all transactions'
                },
              ].map((feature, index) => (
                <Box key={index} sx={{ textAlign: 'center', p: 3 }}>
                  <Typography sx={{ fontSize: '3rem', mb: 2 }}>{feature.icon}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {feature.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Ccircle cx="7" cy="7" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 1200, mx: 'auto' }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '20px',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 3,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="Dhandhapaani"
                  sx={{ width: 56, height: 56, borderRadius: '14px', objectFit: 'cover' }}
                />
              </Box>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  mb: 1,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                  Dhandhapaani
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  opacity: 0.9,
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                }}
              >
                  Welcome to Dhandhapaani
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1,
                  opacity: 0.85,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Powered by Maruti Agritech
              </Typography>
            </Box>
          </Box>

          {/* Hero Headline */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3rem' },
              mb: 3,
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.2,
            }}
          >
            Transform Your Business Operations with
            <Box component="span" sx={{ color: '#ffd700' }}> AI-Powered Intelligence</Box>
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              mb: 4,
              maxWidth: 700,
              mx: 'auto',
              opacity: 0.9,
              lineHeight: 1.6,
            }}
          >
            Dhandhapaani is a business management platform designed to simplify billing, inventory, sales, purchases, and analytics in one place. Dhandhapaani helps teams run daily operations faster with clear, real-time business data.
          </Typography>

          {/* CTA Buttons */}
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap', mb: 6 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => setShowAuthDialog(true)}
              sx={{
                py: 2,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #4285f4 0%, #34a853 25%, #ea4335 50%, #fbbc05 75%, #4285f4 100%)',
                backgroundSize: '300% 300%',
                animation: 'googleGradient 3s ease infinite',
                borderRadius: 3,
                textTransform: 'none',
                boxShadow: '0 8px 32px rgba(66, 133, 244, 0.4)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(66, 133, 244, 0.5)',
                },
              }}
            >
              Get Started Free
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                py: 2,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'white',
                borderRadius: 3,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Watch Demo
            </Button>
          </Box>

          {/* Trust Indicators */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.5rem' }}>🔒</Typography>
              <Typography sx={{ fontSize: '0.9rem', opacity: 0.9 }}>Bank-Level Security</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.5rem' }}>⚡</Typography>
              <Typography sx={{ fontSize: '0.9rem', opacity: 0.9 }}>Setup in Minutes</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.5rem' }}>📊</Typography>
              <Typography sx={{ fontSize: '0.9rem', opacity: 0.9 }}>Real-Time Analytics</Typography>
            </Box>
          </Box>
        </Box>
      </Box>


      {/* Authentication Dialog */}
      <Dialog
        open={showAuthDialog}
        onClose={() => setShowAuthDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ minHeight: 600 }}>
          {/* Logo and Title */}
          <Box sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '15px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Dhandhapaani"
                sx={{ width: 40, height: 40, borderRadius: '12px', objectFit: 'cover' }}
              />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Dhandhapaani
            </Typography>
          </Box>

          {/* Auth Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs value={authTab} onChange={(e, newValue) => setAuthTab(newValue)} variant="fullWidth">
              <Tab label="Sign In" sx={{ fontWeight: 600, textTransform: 'none' }} />
              <Tab label="Sign Up" sx={{ fontWeight: 600, textTransform: 'none' }} />
            </Tabs>
          </Box>

          <DialogContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            {/* Sign In Tab */}
            {authTab === 0 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                  Welcome Back
                </Typography>

                {/* Google Sign In */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading || loading}
                  startIcon={<GoogleIcon />}
                  sx={{
                    py: 1.5,
                    mb: 3,
                    borderColor: '#dadce0',
                    color: '#3c4043',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: '#dadce0',
                      backgroundColor: '#f8f9fa',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Continue with Google'}
                </Button>

                <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                  <Box sx={{ flex: 1, height: 1, background: '#e0e0e0' }} />
                  <Typography sx={{ px: 2, color: '#666', fontSize: '0.9rem' }}>or</Typography>
                  <Box sx={{ flex: 1, height: 1, background: '#e0e0e0' }} />
                </Box>

                {/* Email Sign In Form */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    variant="outlined"
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2 }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleEmailLogin}
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Sign In'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* Sign Up Tab */}
            {authTab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                  Create Your Account
                </Typography>

                {/* Google Sign Up */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading || loading}
                  startIcon={<GoogleIcon />}
                  sx={{
                    py: 1.5,
                    mb: 3,
                    borderColor: '#dadce0',
                    color: '#3c4043',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: '#dadce0',
                      backgroundColor: '#f8f9fa',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Sign up with Google'}
                </Button>

                <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                  <Box sx={{ flex: 1, height: 1, background: '#e0e0e0' }} />
                  <Typography sx={{ px: 2, color: '#666', fontSize: '0.9rem' }}>or</Typography>
                  <Box sx={{ flex: 1, height: 1, background: '#e0e0e0' }} />
                </Box>

                {/* Email Sign Up Form */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={registerData.name}
                    onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                    variant="outlined"
                    size="small"
                    required
                  />

                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    variant="outlined"
                    size="small"
                    required
                  />

                  <TextField
                    fullWidth
                    label="Mobile Number (Optional)"
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                    variant="outlined"
                    size="small"
                    placeholder="+91 9876543210"
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    variant="outlined"
                    size="small"
                    required
                    helperText="Must be at least 6 characters"
                  />

                  <TextField
                    fullWidth
                    label="Confirm Password"
                    type="password"
                    value={registerData.confirmPassword}
                    onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                    variant="outlined"
                    size="small"
                    required
                    sx={{ mb: 3 }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleEmailRegister}
                    disabled={loading}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Create Account'}
                  </Button>
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 4, pb: 3, justifyContent: 'center' }}>
            <Button
              onClick={() => setShowAuthDialog(false)}
              sx={{ color: '#64748b' }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Business Registration Dialog */}
      <Dialog
        open={businessDialog}
        onClose={() => setBusinessDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Complete Your Registration
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Set up your business profile to start managing your operations
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}


            {/* Business Registration Form */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Business Name"
                value={businessData.name}
                onChange={(e) => handleBusinessInputChange('name', e.target.value)}
                required
                variant="outlined"
                placeholder="Enter your business name"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <FormControl fullWidth required>
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={businessData.type}
                  onChange={(e) => handleBusinessInputChange('type', e.target.value)}
                  label="Business Type"
                  sx={{ borderRadius: 2 }}
                >
                  {[
                    'Retail Store',
                    'Wholesale Business',
                    'Manufacturing',
                    'Service Provider',
                    'Restaurant/Food Service',
                    'Healthcare',
                    'Education',
                    'Technology/IT',
                    'Construction',
                    'Transportation',
                    'Real Estate',
                    'Other'
                  ].map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Industry/Sector (Optional)"
                value={businessData.industry}
                onChange={(e) => handleBusinessInputChange('industry', e.target.value)}
                placeholder="e.g., Electronics, Fashion, Food & Beverage"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                fullWidth
                label="Phone Number (Optional)"
                value={businessData.phone}
                onChange={(e) => handleBusinessInputChange('phone', e.target.value)}
                placeholder="+91 9876543210"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                fullWidth
                label="Business Address (Optional)"
                value={businessData.address}
                onChange={(e) => handleBusinessInputChange('address', e.target.value)}
                multiline
                rows={2}
                placeholder="Enter your business address"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="City (Optional)"
                  value={businessData.city}
                  onChange={(e) => handleBusinessInputChange('city', e.target.value)}
                  placeholder="Enter city"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="State/Province (Optional)"
                  value={businessData.state}
                  onChange={(e) => handleBusinessInputChange('state', e.target.value)}
                  placeholder="Enter state/province"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="ZIP/Postal Code (Optional)"
                  value={businessData.zipCode}
                  onChange={(e) => handleBusinessInputChange('zipCode', e.target.value)}
                  placeholder="Enter ZIP/postal code"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Country"
                  value={businessData.country}
                  onChange={(e) => handleBusinessInputChange('country', e.target.value)}
                  placeholder="Enter country"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setBusinessDialog(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBusinessRegistration}
            disabled={loading || !businessData.name || !businessData.type}
            sx={{
              borderRadius: 2,
              px: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              }
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Complete Registration'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced CSS animations */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes googleGradient {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
        `
      }} />
    </Box>
  );
}

export default Auth;


